import i18n from "i18next";
import {
  localizeForVertical,
  type BusinessVertical,
} from "@graftus/schema";
import { NAMESPACES, resources } from "./index";

type Bundle = { [key: string]: string | Bundle };

/** A copy of `bundle` with every string run through `fn`; keys untouched. */
export function mapBundleStrings(bundle: Bundle, fn: (s: string) => string): Bundle {
  const out: Bundle = {};
  for (const [key, value] of Object.entries(bundle)) {
    out[key] = typeof value === "string" ? fn(value) : mapBundleStrings(value, fn);
  }
  return out;
}

/**
 * Subtrees kept verbatim: the business-type picker must name both choices
 * ("Säljorganisation", "Livsmedelsbutik") whatever the current vertical.
 */
const EXEMPT_SUBTREES: Partial<Record<(typeof NAMESPACES)[number], string[]>> = {
  settings: ["vertical"],
  onboarding: ["vertical"],
};

/** The Swedish bundle for one namespace as a vertical sees it. */
export function swedishBundleFor(
  ns: (typeof NAMESPACES)[number],
  vertical: BusinessVertical,
): Bundle {
  const base = resources.sv[ns] as unknown as Bundle;
  if (vertical === "sales") return base;
  const mapped = mapBundleStrings(base, (s) => localizeForVertical(s, vertical));
  for (const key of EXEMPT_SUBTREES[ns] ?? []) {
    if (base[key] !== undefined) mapped[key] = base[key];
  }
  return mapped;
}

let appliedVertical: BusinessVertical = "sales";

/**
 * Swap the Swedish UI terminology for the business's vertical (GRA-83):
 * a DVH store reads "medarbetare", "avdelning" and "butiksdrift" instead of
 * "säljare", "team" and "spelbok". Derived from the shared glossary in
 * `@graftus/schema`, the same source as the coach prompt, so the two never
 * disagree. English is left as is: DVH pilots are Swedish stores.
 */
export function applyVerticalTerminology(vertical: BusinessVertical): void {
  if (vertical === appliedVertical) return;
  for (const ns of NAMESPACES) {
    i18n.addResourceBundle("sv", ns, swedishBundleFor(ns, vertical), true, true);
  }
  appliedVertical = vertical;
  // Re-render translated components on the active language.
  void i18n.changeLanguage(i18n.language);
}
